import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { Amounts } from 'src/types/common/Amounts';
import Avatar from 'src/common/components/Avatar';
import { getPaymentAddons, getTransaction } from 'src/common/utils/reduxStoreSelectors';
import getClassName from 'src/common/utils/getClassName';
import PaymentAddon from 'src/inbox/components/PaymentAddon';
import TripDetailsCard from 'src/common/components/TripDetailsCard';
import { CAPTAIN_OPTION_KEYS, MOMENT_FORMAT } from 'src/common/constants';
import { totalGuests } from 'src/common/components/GroupSizeDetails';
import { formatGroupSize } from 'src/common/helpers';
import getTripCaptainOption from 'src/common/utils/getTripCaptainOption';
import formatPaymentAddons from 'src/inbox/components/PaymentAddon/helper';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import { GUESTS_FIELD, OWNER_OFFER_STATES, TRIP_STATES } from '../../constants';
import { userIsRenter } from '../../helpers';
import useCustomOfferExpiry from '../../../common/hooks/useCustomOfferExpiry';
import CustomOfferExpiry from '../../../common/components/CustomOfferExpiry';
import { ImmutableTrip } from '../../../types/trips/Trips';
import OfferSummary from '../OfferSummary';
import TermsAndCancellation from '../TermsAndCancellation';
import LocationOverview from '../LocationOverview';

import s from './TripDetails.module.scss';
import CostBreakdown from '../CostBreakdown';

// const dateTimeFormatPattern = 'YYYY-MM-DD HH:mm';
const timeFormatPattern = MOMENT_FORMAT.TIME;
const dateFormatPattern = 'ddd MMM D, YYYY';

type TripDetailsProps = {
  renderComments?: boolean;
  trip: ImmutableTrip;
  withTooltips?: boolean;
  withAddons?: boolean;
  /** Calendar should omit CostBreakdown until transaction is reliable */
  showCostBreakdown?: boolean;
};

const TripDetails: FC<TripDetailsProps> = ({
  renderComments = true,
  trip,
  withTooltips = true,
  withAddons = false,
  showCostBreakdown = true,
}) => {
  const transaction = useSelector(getTransaction);
  const tripState = trip.getIn(['state', 'state']);
  const boat = trip.get('boat');
  const url = trip.getIn(['boat', 'listing_url']);
  const {
    offerTrip: {
      startDateTimeMoment,
      endDateTimeMoment,
      durationAsWords,
    },
  } = useSelector(dateTimeDurationSelector(trip.get('pk')));
  // This normalization is necessary because `None` and `Book now!` is erroneously returned.
  // The API returns `Book now!` for Instabooks that are created with the bulk creation
  // tool in Bridge
  const renterComment = trip.get('renter_comment') !== 'None' ? trip.get('renter_comment') : '';
  const initialOwnerComment = trip.get('owner_comment');
  const ownerComment = initialOwnerComment !== 'None' && initialOwnerComment !== 'Book now!'
    ? initialOwnerComment : '';

  const isRenter = userIsRenter(trip);
  const amounts: Amounts = transaction?.get('amounts')?.toJS()
    || trip?.getIn(['transaction', 'amounts'])?.toJS();

  const currency = amounts?.[isRenter ? 'renter_currency' : 'owner_currency'];
  const deposit = amounts?.[isRenter ? 'booking_deposit' : 'listing_deposit'];
  const cancellationPolicy = amounts?.cancellation_policy;

  const { renderCustomExpiry } = useCustomOfferExpiry(trip);

  const shouldRenderPrice = (
    showCostBreakdown
    && ![TRIP_STATES.OWNER_CHANGES_REQUESTED, TRIP_STATES.OWNER_OFFER_CANCELLED].includes(tripState)
    && !transaction.isEmpty()
  );
  const isInstabook = trip.get('is_instabook');

  const instabookRenterOccasion = isInstabook && renterComment?.split('<br/>')[0];
  const instabookRenterMessage = isInstabook && renterComment?.split('<br/>')[1];

  const { ownerOptionalAmounts, paymentAddons } = useSelector(getPaymentAddons);

  const captainOption = getTripCaptainOption(trip);
  const captainOptionText = captainOption === CAPTAIN_OPTION_KEYS.PROVIDED
  || captainOption === CAPTAIN_OPTION_KEYS.ARRANGED_SEPARATELY
    ? 'With Captain'
    : 'Self Captain';

  const locations = trip.getIn(['boat', 'locations'])?.toJS();

  return (
    <ul className={s.root}>
      <li className={s.rootItem}>
        <TripDetailsCard
          titleLayout="row"
          variant="white"
          listingId={boat.get('id')}
          headline={boat.get('headline')}
          listingPhoto={boat.get('photo_url')}
          departureDate={startDateTimeMoment.format(dateFormatPattern)}
          returnDate={endDateTimeMoment.format(dateFormatPattern)}
          departureTime={startDateTimeMoment.format(timeFormatPattern)}
          returnTime={endDateTimeMoment.format(timeFormatPattern)}
          duration={durationAsWords}
          capacity={formatGroupSize(totalGuests(trip.get('guests')), GUESTS_FIELD)}
          captainOption={captainOptionText}
        />
      </li>
      {renderComments && renterComment && (
        <li className={getClassName(s, 'rootItem', 'comment outlined')}>
          <h6 className={s.author}>
            <Avatar
              classNameModifier="noMargin"
              size="tiny"
              user={trip.get('renter').toJS()}
            />
            {trip.getIn(['renter', 'first_name'])}
          </h6>
          {isInstabook ? (
            <>
              {instabookRenterOccasion && (
                <p className={s.tripDescription}>
                  {instabookRenterOccasion}
                </p>
              )}
              {instabookRenterMessage && (
                <p className={s.tripDescription}>
                  {instabookRenterMessage}
                </p>
              )}
            </>
          ) : (
            <p className={s.tripDescription}>
              {renterComment}
            </p>
          )}
        </li>
      )}
      {renderComments && ownerComment && (
        <li className={getClassName(s, 'rootItem', 'comment outlined')}>
          {isInstabook ? (
            <h4 className={s.title}>
              Trip Description
            </h4>
          ) : (
            <h6 className={s.author}>
              <Avatar
                classNameModifier="noMargin"
                size="tiny"
                user={trip.get('owner').toJS()}
              />
              {trip.getIn(['owner', 'first_name'])}
            </h6>
          )}
          <p className={s.tripDescription}>
            {ownerComment}
          </p>
        </li>
      )}
      {shouldRenderPrice && OWNER_OFFER_STATES.includes(tripState) && (
        <li className={s.rootItem}>
          <OfferSummary
            amounts={amounts}
            theme="dark"
          />
        </li>
      )}
      {shouldRenderPrice && amounts?.price_sections?.map((priceSection) => (
        <li
          key={priceSection.id}
          className={s.rootItem}
        >
          <CostBreakdown
            open={false}
            priceSection={priceSection}
            theme="light"
          />
        </li>
      ))}
      {withAddons && !!(paymentAddons?.length || ownerOptionalAmounts?.length) && (
        <div className={s.addons}>
          <p className={s.title}>Add-ons</p>
          <div className={s.cards}>
            {paymentAddons.map((addon) => (
              <PaymentAddon
                key={addon.pk}
                {...formatPaymentAddons({ addon, isOwner: !isRenter })}
              />
            ))}
            {ownerOptionalAmounts.map((addon) => (
              <PaymentAddon
                key={addon.id}
                expiryDate={trip.get('reservation_confirmation_date')}
                lastActivityDate={trip.get('reservation_confirmation_date')}
                status="paid"
                paymentCategory={addon.description ?? ''}
                paymentAmount={`${addon.currency} ${addon.subtotal}`}
                paymentLink=""
                userRole={isRenter ? 'renter' : 'owner'}
              />
            ))}
          </div>
        </div>
      )}
      {locations.length > 0 && (
        <li className={s.rootItem}>
          <LocationOverview
            locations={locations}
          />
        </li>
      )}
      {renderCustomExpiry && (
        <li className={getClassName(s, 'rootItem', 'customExpiry')}>
          <CustomOfferExpiry
            trip={trip}
          />
        </li>
      )}
      {shouldRenderPrice && (
        <li className={s.rootItem}>
          <TermsAndCancellation
            currency={currency}
            deposit={deposit}
            listingUrl={url}
            cancellationPolicy={cancellationPolicy}
            withTooltips={withTooltips}
            freeCancellationInMarketplace={amounts?.free_cancellation_in_marketplace}
          />
        </li>
      )}
    </ul>
  );
};

export default TripDetails;
